import { type BookmarksThroughputChartAttributes } from './generated/BookmarksThroughputChartAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import DateRangeFilter from '../filters/DateRangeFilter';
import EstimateTypeFilter from '../filters/EstimateTypeFilter';
import TeamFilter from '../filters/TeamFilter';
import UserFilter from '../filters/UserFilter';
import Fragment from '../Fragment';
import { model } from '../modelBuilder';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import Project from './Project';
import User from './User';
import {
  type RecordEventFilters,
  type RecordEventTimeSeries,
} from './generated/types';
import { RecordEventTypeEnum } from '../Enums';
import { nameMap } from '../lib/nameMap';

export const DateGroupings = {
  WEEK: 'Weeks',
  MONTH: 'Months',
};
interface BookmarksThroughputChart
  extends BookmarksThroughputChartAttributes,
    HasBookmarkFilters<{
      dateRange: DateRangeFilter;
      teams: TeamFilter;
      users: UserFilter;
      estimation_type: EstimateTypeFilter;
    }> {}

@model
@HasBookmarkFilters
class BookmarksThroughputChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksThroughputChart' = 'BookmarksThroughputChart';
  get typename(): 'BookmarksThroughputChart' {
    return 'BookmarksThroughputChart';
  }

  static internalTypename: 'Bookmarks::ThroughputChart' =
    'Bookmarks::ThroughputChart';
  get internalTypename(): 'Bookmarks::ThroughputChart' {
    return 'Bookmarks::ThroughputChart';
  }

  static bookmarkName = 'Bookmarks::ThroughputChart';

  static inputType = 'ThroughputChartAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    timeGroup: attr({ writable: true, readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  /**
   * Initialize the filters
   */
  async createFilters() {
    const [teams, users] = await Promise.all([
      this.selectedTeams(),
      this.availableUsers(),
    ]);

    const userFilter = new UserFilter(
      this.filters.users || [],
      users.map(nameMap)
    );

    const estimateTypeFilter = new EstimateTypeFilter({
      initialItems: this.filters.estimation_type || ['count'],
      capacityUnits: this.project?.defaultCapacityUnits || 'POINTS',
    });

    return {
      dateRange: new DateRangeFilter(this.filters.dateRange),
      teams: new TeamFilter(
        teams.map(([, id]) => id),
        teams
      ),
      users: userFilter,
      estimation_type: estimateTypeFilter,
    };
  }

  canFilter(_scope) {
    return true;
  }

  async selectedTeams(): Promise<[string, RecordId][]> {
    if (!this.project?.isProductLine) {
      return [];
    }
    const childIds = this.project?.children.map(c => c.id) || [];
    const teamIds = this.filters.teams?.length ? this.filters.teams : childIds;

    if (teamIds.length > 0) {
      const teams = await Project.select(['id', 'name'])
        .where({ id: teamIds })
        .all();

      return teams.map(t => [t.name, t.id]);
    }
    return [];
  }

  /**
   * Load all the users available to this team
   */
  async availableUsers() {
    if (!this.local.users)
      this.local.users = await User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId })
        .per(100)
        .all();
    return this.local.users;
  }

  /**
   * Load the selected user
   */
  async user() {
    // @ts-ignore filters are untyped
    const userId = this.getFilter('users').single() as string;
    if (!userId) return null;
    const users = await this.availableUsers();
    return users.find(u => u.id === userId);
  }

  /**
   * Load the selected unit
   *
   * @return {Aha.WorkUnitEnum['values'] | 'COUNT'}
   */
  units() {
    // @ts-ignore filters are untyped
    const units = this.getFilter('estimation_type').single();
    if (units === 'count') {
      return 'COUNT';
    } else {
      return this.project?.defaultCapacityUnits || 'POINTS';
    }
  }

  async chartData() {
    const timePeriod = this.getFilter('dateRange') as DateRangeFilter;
    // @ts-ignore filters are untyped
    const estimationType = this.getFilter('estimation_type').single();
    const user = await this.user();
    const teams = await this.selectedTeams();

    const timeSeriesFragment = new Fragment('timeSeries')
      .argument('timeGroup', this.timeGroup, 'RecordEventTimeSeriesGroupEnum!')
      .argument(
        'aggregation',
        estimationType === 'count' ? 'COUNT' : 'SUM',
        'RecordEventValueOperationEnum'
      )
      .fragment(new Fragment('seriesRange').attr('from', 'to'))
      .attr(
        'eventTimeIndex',
        'eventType',
        'units',
        'originalEstimate',
        'remainingEstimate'
      );

    const baseFilters: RecordEventFilters = {
      createdAt: {
        gt: timePeriod.startTime.startOf('day').toISOString(),
        lt: timePeriod.endTime.add(1, 'day').startOf('day').toISOString(),
      },
      eventType: [RecordEventTypeEnum.RECORD_COMPLETED],
      includeFeaturesUsingRequirementsEstimates: this.units() === 'COUNT',
    };

    // If the estimation type is time then make sure all records are using the
    // same units
    if (estimationType === 'time') {
      baseFilters.units = this.project?.defaultCapacityUnits || 'POINTS';
    }

    if (user) {
      baseFilters.assignedToUserId = user.id;
    }

    // If we have multiple teams selected, fetch data for each team
    if (teams.length > 0 && this.project?.isProductLine) {
      const teamData = await Promise.all(
        teams.map(async ([teamName, teamId]) => {
          const filters = { ...baseFilters, teamId };
          const fragment = new Fragment('recordEvents')
            .argument('filters', filters, 'RecordEventFilters!')
            .fragment(timeSeriesFragment);

          const data = (await ApplicationModel.query(fragment)) as any;
          return {
            teamId,
            teamName,
            timeSeries: data.recordEvents.timeSeries as RecordEventTimeSeries[],
          };
        })
      );

      return teamData;
    } else {
      // Single team mode (backward compatible)
      const filters = { ...baseFilters, teamId: this.projectId };
      const fragment = new Fragment('recordEvents')
        .argument('filters', filters, 'RecordEventFilters!')
        .fragment(timeSeriesFragment);

      const data = (await ApplicationModel.query(fragment)) as any;
      return data.recordEvents.timeSeries as RecordEventTimeSeries[];
    }
  }
}

export default BookmarksThroughputChart;
